import { makeAutoObservable, runInAction } from 'mobx';

class UserStore {
  accessToken = null;
  profile = null;
  loading = false;
  error = null;
  isAuthenticated = false;
  initializing = true;
  rootStore = null;
  messages = []; // Add messages array like mobile app
  onboardingData = null; // Store onboarding data like mobile app

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // Initialize the user store
  initialize() {
    // Check if user is already authenticated from localStorage
    const token = localStorage.getItem('biscuit_access_token');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      runInAction(() => {
        this.accessToken = token;
        this.profile = JSON.parse(user);
        this.isAuthenticated = true;
        this.initializing = false;
      });
    } else {
      runInAction(() => {
        this.initializing = false;
      });
    }
  }

  // Set loading state
  setLoading(loading) {
    this.loading = loading;
  }

  // Set error state
  setError(error) {
    this.error = error;
  }

  // Set authentication state
  setAuthState(isAuthenticated, accessToken = null, profile = null) {
    this.isAuthenticated = isAuthenticated;
    this.accessToken = accessToken;
    this.profile = profile;
  }

  // Fetch user profile from backend
  async fetchProfile() {
    if (!this.accessToken) return;

    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.rootStore.authenticatedApiCall('api/users/me');
      
      console.log('Profile response:', response);
      console.log('onboarding_complete:', response.onboarding_complete);
      
      runInAction(() => {
        this.profile = response;
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching profile:', error);
      runInAction(() => {
        this.setError(error.response?.data?.message || 'Failed to fetch profile');
        // If token is invalid, clear auth state
        if (error.message?.includes('401')) {
          this.setAuthState(false);
        }
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  // Update user profile
  async updateProfile(profileData) {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.rootStore.authenticatedApiCall(
        'api/users/me',
        {},
        'PUT',
        profileData
      );

      runInAction(() => {
        this.profile = { ...this.profile, ...response };
      });
      
      return { success: true };
    } catch (error) {
      runInAction(() => {
        this.setError(error.message || 'Failed to update profile');
      });
      return { success: false, error: this.error };
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  // Complete onboarding
  async completeOnboarding(onboardingData) {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.rootStore.authenticatedApiCall(
        'api/users/me/onboarding',
        {},
        'PATCH',
        onboardingData
      );

      runInAction(() => {
        this.profile = { ...this.profile, ...response };
      });
      
      return { success: true };
    } catch (error) {
      runInAction(() => {
        this.setError(error.message || 'Failed to complete onboarding');
      });
      return { success: false, error: this.error };
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  // Upload avatar
  async uploadAvatar(imageFile) {
    this.setLoading(true);
    this.setError(null);

    try {
      const formData = new FormData();
      formData.append('avatar', imageFile);

      const response = await this.rootStore.authenticatedApiCall(
        'api/users/me/avatar',
        {},
        'PATCH',
        formData,
        true // file upload
      );

      runInAction(() => {
        this.profile = { ...this.profile, ...response };
      });
      
      return { success: true };
    } catch (error) {
      runInAction(() => {
        this.setError(error.message || 'Failed to upload avatar');
      });
      return { success: false, error: this.error };
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  // Send message (chat functionality)
  async sendMessage(message) {
    if (!message.trim()) return;

    // Add user message to the list immediately (like mobile app)
    const newMessage = {
      role: "user",
      id: Date.now(),
      content: [
        {
          text: {
            value: message,
          },
        },
      ],
      created_at: new Date().toISOString()
    };

    const loadingMessage = {
      role: "loading",
      id: Date.now() + 1,
      content: [
        {
          text: {
            value: "Coach Biscuit is thinking..."
          }
        }
      ],
      created_at: new Date().toISOString()
    };

    const newMessages = [loadingMessage, newMessage, ...this.messages];

    runInAction(() => {
      this.messages = newMessages;
    });

    try {
      const response = await this.rootStore.authenticatedApiCall(
        "api/ai/message",
        {},
        "POST",
        {
          text: message,
        }
      );

      runInAction(() => {
        this.messages = response.messages;
      });
    } catch (error) {
      console.log('Error sending message:', error);
      runInAction(() => {
        // Remove loading message on error
        this.messages = this.messages.filter(msg => msg.role !== 'loading');
      });
    }
  }

  // Fetch messages (chat functionality)
  async fetchMessages() {
    console.log('🔍 UserStore.fetchMessages: Starting...');
    console.log('🔍 UserStore.fetchMessages: Access token:', !!this.accessToken);
    console.log('🔍 UserStore.fetchMessages: Access token value:', this.accessToken);
    try {
      const data = await this.rootStore.authenticatedApiCall("api/ai/messages");
      console.log('🔍 UserStore.fetchMessages: Response received:', data);
      
      if (data.error && data.status === 401) {
        this.clearAuthState();
        return;
      }

      // If there are no messages, let's add a welcome message (like mobile app)
      if (data.messages.length === 0) {
        const newMessage = {
          role: "assistant",
          id: Date.now(),
          content: [
            {
              text: {
                value:
                  "Hello! My name is Coach Biscuit and I am here to help you get better at hockey! Ask me anything! I can come up with drills, help you with your game, team dynamics or just chat! Let's get started by telling me your name, your age and your level of play!",
              },
            },
          ],
          created_at: new Date().toISOString()
        };
        data.messages.push(newMessage);
      }

      runInAction(() => {
        console.log('🔍 UserStore.fetchMessages: Setting messages:', data.messages);
        this.messages = data.messages;
      });

      return data;
    } catch (error) {
      console.log('Error fetching messages:', error);
      // Don't clear auth state on error, just log it like mobile app
    }
  }

  // Getter for login status
  get isLoggedIn() {
    return this.isAuthenticated && this.accessToken !== null;
  }

  // Getter for profile completion
  get hasProfile() {
    console.log('hasProfile check:', {
      hasProfile: this.profile,
      onboarding_complete: this.profile?.onboarding_complete,
      profile: this.profile
    });
    return this.profile && this.profile.onboarding_complete;
  }

  // Getter for display name
  get displayName() {
    if (!this.profile) return '';
    return `${this.profile.first_name || ''} ${this.profile.last_name || ''}`.trim();
  }

  // Getter for avatar
  get avatar() {
    return this.profile?.avatar || null;
  }

  // Getter for user ID
  get userId() {
    return this.profile?.id || null;
  }

  // Getter for user email
  get userEmail() {
    return this.profile?.email || '';
  }

  // Clear authentication state
  clearAuthState() {
    localStorage.removeItem('biscuit_access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('user');
    
    runInAction(() => {
      this.accessToken = null;
      this.profile = null;
      this.isAuthenticated = false;
    });
  }
}

export { UserStore }; 